import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Calendar,
  Clock,
  MapPin,
  Loader2,
  AlertCircle,
  Download,
  FileText,
  Users
} from 'lucide-react';
import PublicLayout from '@shared/ui/PublicLayout';
import Chip from '../components/Chip';

// API client without auth
const publicApi = axios.create({
  baseURL: '/api',
});

interface EventBill {
  bill_id: number;
  bill_number: string;
  bill_title: string;
}

interface CalendarEvent {
  calendar_id: number;
  reading_date: string;
  event_type: string;
  event_type_display: string;
  chamber: string;
  time_display?: string;
  location?: string;
  location_display?: string;
  description?: string;
  committee_name?: string;
  bills?: EventBill[];
}

interface EventMetadata {
  title: string;
  subtitle: string;
  date: string;
  description: string;
}

interface PublicEventData {
  event: CalendarEvent;
  metadata: EventMetadata;
}

// Fetch event data
const fetchPublicEvent = async (eventId: string): Promise<PublicEventData> => {
  const response = await publicApi.get(`/public/calendar/events/${eventId}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to load event');
  }
  return response.data.data;
};

// Download ICS file
const downloadICS = async (eventId: number): Promise<void> => {
  const response = await publicApi.get(`/public/calendar/events/${eventId}/export.ics`, {
    responseType: 'blob'
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = `event-${eventId}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * PublicCalendarEventPreviewPage - Public view of a calendar event
 * 
 * Shows event details without requiring authentication.
 * Used by Outreach links.
 */
const PublicCalendarEventPreviewPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();

  // Fetch event data
  const { data: eventData, isLoading, error } = useQuery(
    ['public-event-preview', eventId],
    () => fetchPublicEvent(eventId!),
    {
      enabled: !!eventId,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  // Handle ICS download
  const handleDownloadICS = async () => {
    if (!eventData?.event?.calendar_id) return;
    try {
      await downloadICS(eventData.event.calendar_id);
      toast.success('Calendar event downloaded');
    } catch (err) {
      toast.error('Failed to download calendar event');
    }
  };

  // Format display date
  const formatDisplayDate = (dateStr: string) => {
    try {
      const dateOnly = (dateStr || '').split('T')[0];
      const date = new Date(dateOnly + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <PublicLayout showFooter={false}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-3 text-text-secondary dark:text-text-dark-secondary">
            Loading event...
          </span>
        </div>
      </PublicLayout>
    );
  }

  // Error state
  if (error || !eventData) {
    return (
      <PublicLayout showFooter={true} loginReturnUrl="/calendar">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
            Event Not Found
          </h2>
          <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
            {error instanceof Error ? error.message : 'The event could not be found.'}
          </p>
          <Link 
            to="/"
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Go to Home
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const { event, metadata } = eventData;

  const eventDate = event.reading_date
    ? (typeof event.reading_date === 'string'
        ? event.reading_date.split('T')[0]
        : String(event.reading_date))
    : null;

  return (
    <PublicLayout 
      organizationName="Colorado Legislature"
      showFooter={true}
      loginReturnUrl={eventDate ? `/calendar/${eventDate}` : '/calendar'}
    >
      {/* Hero Section */}
      <div className="card p-4 sm:p-6 mb-3 sm:mb-4 lg:mb-6">
        {/* Event Type and Chamber */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Chip 
            label={event.event_type_display || event.event_type || 'Event'} 
            variant="primary"
            size="sm"
          />
          {event.chamber && (
            <Chip 
              label={event.chamber} 
              variant="neutral"
              size="sm"
            />
          )}
        </div>

        {/* Event Title */}
        <h1 className="text-xl sm:text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-3">
          {metadata.title || event.committee_name || 'Legislative Event'}
        </h1>

        {/* Description */}
        {(metadata.description || event.description) && (
          <p className="text-text-secondary dark:text-text-dark-secondary text-sm mb-4">
            {metadata.description || event.description}
          </p>
        )}

        {/* Meta Info */}
        <div className="space-y-2 text-sm">
          {/* Date */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0" />
            <span className="text-text-primary dark:text-text-dark-primary font-medium">
              {formatDisplayDate(metadata.date || event.reading_date)}
            </span>
          </div>

          {/* Time */}
          {event.time_display && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0" />
              <span className="text-text-secondary dark:text-text-dark-secondary">
                {event.time_display}
              </span>
            </div>
          )}

          {/* Location */}
          {(event.location_display || event.location) && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0" />
              <span className="text-text-secondary dark:text-text-dark-secondary">
                {event.location_display || event.location}
              </span>
            </div>
          )}

          {/* Committee */}
          {event.committee_name && (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0" />
              <span className="text-text-secondary dark:text-text-dark-secondary">
                {event.committee_name}
              </span>
            </div>
          )}
        </div>

        {/* Download ICS Button */}
        <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
          <button
            onClick={handleDownloadICS}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Add to Calendar
          </button>
        </div>
      </div>

      {/* Related Bills */}
      {event.bills && event.bills.length > 0 && (
        <div className="card p-4 mb-3 sm:mb-4 lg:mb-6">
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
            Related Bills
          </h2>
          <div className="space-y-2">
            {event.bills.map((bill, idx) => (
              <div 
                key={idx}
                className="py-2 border-b border-border-light dark:border-border-dark last:border-0"
              >
                <Link 
                  to={`/public/bills/${bill.bill_number}`}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium text-sm"
                >
                  {bill.bill_number}
                </Link>
                {bill.bill_title && (
                  <p className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-2 mt-0.5">
                    {bill.bill_title}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </PublicLayout>
  );
};

export default PublicCalendarEventPreviewPage;
